<script setup lang="ts">
import { NuxtLink } from '#components';

const props = withDefaults(
  defineProps<{
    page?: number
    itemsPerPage?: number
    totalItems?: number
    titleTranslationKey: string
  }>(),
  {
    page: 1,
    itemsPerPage: 30,
    totalItems: 0,
  },
);

const shouldDisplay = computed(() => props.totalItems > props.itemsPerPage);

// TITLE DISPLAY
const { n } = useI18n();
const paginatedElementsCount = computed(() => ({
  itemsStart: n(((props.page - 1) * props.itemsPerPage) + 1),
  itemsEnd: n(Math.min((props.page) * props.itemsPerPage, props.totalItems)),
  totalItems: n(props.totalItems),
}));

// UNIT PAGE DISPLAY
const isMounted = useMounted();
const route = useRoute();
// Cap under 10 000 items
const cappedItems = computed(() => Math.min(props.totalItems, (Math.floor(10_000 / props.itemsPerPage) * props.itemsPerPage)));
const pageCount = computed(() => Math.ceil((cappedItems.value || 0) / (props.itemsPerPage || 1)));

const prevPage = computed(() => {
  const disabled = props.page <= 1;
  const pageIndex = Math.max(1, props.page - 1);
  if (disabled || !isMounted.value) return { disabled };

  return {
    // force empty query if we are on the first page
    to: { path: route.path, query: { ...route.query, page: pageIndex === 1 ? undefined : pageIndex } },
  };
});

const nextPage = computed(() => {
  const disabled = props.page >= pageCount.value;
  const pageIndex = Math.min(pageCount.value, props.page + 1);
  if (disabled || !isMounted.value) return { disabled };
  return {
    to: { path: route.path, query: { ...route.query, page: pageIndex } },
  };
});

const pages = computed(() => {
  // always display for page
  const pages: Array<number | `ellipsis-before` | `ellipsis-after`> = [1];
  // we display 10 pages at a time always staring from the current ten
  const currentTen = Math.floor(props.page / 10);
  const totalTen = Math.floor(pageCount.value / 10);
  // when first page is separated from the rest displays an ellipsis
  if (currentTen > 0) pages.push(`ellipsis-before`);
  for (let i = 0; i < 10; i++) {
    const computedPage = Math.min(i + (currentTen * 10), pageCount.value);
    if (computedPage === 0) continue;
    // make sure by displaying the next 10 pages we don't go over the total page count
    pages.push(computedPage);
  }
  // when last page is separated from the rest displays an ellipsis
  if (currentTen !== totalTen) pages.push(`ellipsis-after`);
  pages.push(pageCount.value);
  // remove duplicates & remap to useful data
  return Array.from(new Set(pages)).map((pageNumber) => {
    const isCurrentPage = pageNumber === props.page;
    const isPageEllipsis = typeof pageNumber === `string`;
    const isLink = !isCurrentPage && !isPageEllipsis;
    const component = isLink ? NuxtLink : `span`;
    const text = isPageEllipsis ? `…` : pageNumber;
    // force empty query if we are on the first page
    const to = isLink
      ? { path: route.path, query: { ...route.query, page: pageNumber === 1 ? undefined : pageNumber } }
      : null;

    return {
      id: pageNumber,
      component,
      text,
      isCurrentPage,
      to,
    };
  });
});

// TEN/HUNDRED PAGE DISPLAY
const tenInHundredPages = computed(() => {
  const pages: Array<number> = [];
  const currentHundred = Math.floor(props.page / 100);
  const currentTen = Math.floor(props.page / 10);
  const maxHundred = Math.floor(pageCount.value / 100);
  const maxTen = pageCount.value;
  for (let i = 0; i < 10; i++) {
    const tenInHundredToAdd = Math.min((i * 10) + currentHundred * 100, maxTen);
    // Current Ten is omitted
    if (tenInHundredToAdd === currentTen) continue;
    pages.push(tenInHundredToAdd);
  }
  // Go from hundred to hundred
  for (let i = 0; i <= maxHundred; i++) {
    const hundredToAdd = Math.min(i * 100, maxTen * 10);
    pages.push(hundredToAdd);
  }
  // Always display the last ten of ending hundred
  const lastHundred = pages.at(-1);
  for (let i = 0; i < 10; i++) {
    const tenInHundredToAdd = Math.min((i * 10) + (lastHundred ?? 0), Math.floor(maxTen / 10) * 10);
    pages.push(tenInHundredToAdd);
  }
  // remove duplicates
  return Array.from(new Set(pages)).filter(number => number > 0).sort((a, b) => a - b);
});
</script>

<template>
  <div
    v-if="shouldDisplay"
    class="py-l"
  >
    <p
      class="text-center font-semibold"
      v-text="$t(titleTranslationKey, paginatedElementsCount)"
    />
    <nav class="flex flex-wrap gap-xs items-center justify-center pt-s">
      <IadBtn
        icon
        rel="prev"
        v-bind="prevPage"
        class="me-m"
        variant="outlined"
      >
        <Icon name="iad-icon:caret-left" />
      </IadBtn>
      <component
        :is="paginatedPage.component"
        v-for="paginatedPage of pages"
        :key="paginatedPage.id"
        class="size-12 flex flex-wrap items-center justify-center rounded-full"
        :class="{
          'bg-primary-40 text-white': paginatedPage.isCurrentPage,
          'hover:bg-primary-40 hover:text-white': !paginatedPage.isCurrentPage && paginatedPage.to,
        }"
        :to="paginatedPage.to"
      >
        {{ paginatedPage.text }}
      </component>
      <IadBtn
        icon
        rel="next"
        v-bind="nextPage"
        variant="outlined"
        class="ms-m"
      >
        <Icon name="iad-icon:caret-right" />
      </IadBtn>
    </nav>
    <nav
      v-if="tenInHundredPages.length > 1"
      class="flex flex-wrap gap-xs items-center justify-center pt-s text-sm"
    >
      <NuxtLink
        v-for="tenInHundred in tenInHundredPages"
        :key="tenInHundred"
        :to="{ path: route.path, query: { ...route.query, page: tenInHundred } }"
        class="after:content-['_•_'] after:inline-block after:ps-xs last:after:content-none"
      >
        <span
          class="after:content-['+']"
          v-text="tenInHundred"
        />
      </NuxtLink>
    </nav>
  </div>
</template>
